import { useState, useMemo, useCallback } from "react";
import Table, { TableHeading, TableControls, TableFooter, buildActionColumn } from "@/components/Table";
import type { TableAction } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { Controller, useForm } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import MultiSelect from "@/components/MultiSelect";
import usePagination from "@/hooks/usePagination";
import type { ColumnDef, CellContext, SortingState, OnChangeFn } from "@tanstack/react-table";
import { useParams as useSearchParams, useSearchWithParams } from "@/hooks/useSearchParams";
import AvatarDetails from "@/components/AvatarDetails";
import { ParticipantRow } from "../types";
import DateSelect from "@/components/DateSelect";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { DeleteParticipationModal, EditParticipationModal } from "@/features/participation";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useNavigate, Path } from "@/router";
import { useQuery } from "@apollo/client";
import { GET_PARTICIPANTS, ParticipantsQueryData } from "../../queries";
import { toUTCFormat } from "@/lib/dateHelpers";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { renderParticipationRole, renderAttendanceType } from "../../_utils";
import { ActivityParticipationRole, AttendanceType } from "../../types";
import { getParticipationRoleOptions, getAttendanceTypeOptions } from "../../_utils";
import { ActivityTypeOutletContext } from "../_layout";
import { useLocation, useOutletContext } from "react-router-dom";
import { buildParticipationActions } from "../participationUtils";

const SORT_FIELD_MAP: Record<string, string> = {
  name: "PERSON_NAME",
  title: "TITLE",
  role: "ROLE",
  attendanceType: "ATTENDANCE_TYPE",
  startDate: "BEGAN_AT",
  submittedAt: "CREATED_AT",
  creditsAwarded: "CREDITS_ACCEPTED"
};

export interface ParticipantsTableProps {
  staticFilters: Record<string, unknown>;
  extraColumns: ColumnDef<ParticipantRow, unknown>[];
  headingBody: string;
  additionalHeadingActions?: TableAction[];
  exportHeaders: string[];
  exportRow: (participant: ParticipantRow) => (string | number | null | undefined)[];
  exportFilename?: string;
  onExportCurrentList?: () => void | Promise<void>;
  isExportingCurrentList?: boolean;
  testId?: string;
  noDataCaption?: string;
  activityTypeId: string;
  persistDateRange?: boolean;
  showRoleFilter?: boolean;
  showAttendanceTypeFilter?: boolean;
  canManageParticipationActions?: boolean;
}

export default function ParticipantsTable({
  staticFilters,
  extraColumns,
  headingBody,
  additionalHeadingActions,
  exportHeaders,
  exportRow,
  exportFilename = "activity-participants.csv",
  onExportCurrentList,
  isExportingCurrentList = false,
  testId = "participants-table",
  noDataCaption,
  activityTypeId,
  persistDateRange = false,
  showRoleFilter = true,
  showAttendanceTypeFilter = true,
  canManageParticipationActions = false
}: ParticipantsTableProps) {
  const { addSearchParam, deleteSearchParams, params } = useSearchParams();
  const { activityData } = useOutletContext<ActivityTypeOutletContext>();
  const creditRates =
    activityData?.__typename === "ActivityType"
      ? (activityData?.config?.creditRates ?? [])
      : (activityData?.activityType?.config?.creditRates ?? []);
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm",
    debounceMs: 300
  });
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [orderBy, setOrderBy] = useOrderByParam();
  const navigate = useNavigate();
  const location = useLocation();
  const { Menu, setMenu } = useTableRowMenu();

  const form = useForm({
    defaultValues: { dateRange: { startDate: "", endDate: "" } }
  });
  const { control, setValue } = form;

  const [dateRange, setDateRange] = useState({
    startDate: persistDateRange ? (params.startDate ?? "") : "",
    endDate: persistDateRange ? (params.endDate ?? "") : ""
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedAttendanceTypes, setSelectedAttendanceTypes] = useState<string[]>([]);

  const filterState = {
    dateRange: !!(dateRange.startDate || dateRange.endDate),
    roles: selectedRoles.length > 0,
    attendanceTypes: selectedAttendanceTypes.length > 0
  };
  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const handleClearAllFilters = useCallback(() => {
    clearSearch();
    setDateRange({ startDate: "", endDate: "" });
    setValue("dateRange", { startDate: "", endDate: "" });
    setSelectedRoles([]);
    setSelectedAttendanceTypes([]);
    if (persistDateRange) deleteSearchParams(["searchTerm", "startDate", "endDate"]);
    deleteSearchParams(["sortField", "sortDir"]);
    resetPagination();
  }, [clearSearch, setValue, persistDateRange, deleteSearchParams, resetPagination]);

  const queryFilters = useMemo(
    () => ({
      ...staticFilters,
      search: debouncedSearch || undefined,
      ...(dateRange.startDate && dateRange.endDate
        ? { dateRange: { from: dateRange.startDate, to: dateRange.endDate } }
        : {}),
      ...(selectedRoles.length > 0 ? { roles: selectedRoles } : {}),
      ...(selectedAttendanceTypes.length > 0 ? { attendanceTypes: selectedAttendanceTypes } : {})
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(staticFilters), debouncedSearch, dateRange, selectedRoles, selectedAttendanceTypes]
  );

  const orderByVariable = useMemo(() => {
    const sort = orderBy[0];
    if (!sort) return undefined;
    const field = SORT_FIELD_MAP[sort.id];
    if (!field) return undefined;
    return { field, direction: sort.desc ? "DESC" : "ASC" };
  }, [orderBy]);

  const { data, loading: participantsLoading } = useQuery<ParticipantsQueryData>(GET_PARTICIPANTS, {
    variables: { filters: queryFilters, orderBy: orderByVariable, ...paginationVariables }
  });

  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const [selectedParticipation, setSelectedParticipation] = useState<ParticipantRow | null>(null);
  const [editParticipationModalOpen, setEditParticipationModalOpen] = useState(false);
  const [deleteParticipationModalOpen, setDeleteParticipationModalOpen] = useState(false);

  const participantsData = useMemo((): ParticipantRow[] => {
    if (!data?.activityParticipations?.nodes) return [];
    return data.activityParticipations.nodes.map(
      (participant): ParticipantRow => ({
        id: participant.id,
        name: participant.person.firstName + " " + participant.person.lastName,
        pgaId: participant.person.pgaId ?? "",
        creditId: participant.credit?.id ?? null,
        canEdit: participant.canEdit,
        canDelete: participant.canDelete,
        title: participant.activity?.title ?? participant.title,
        isTypeLevelParticipation: participant.isTypeLevelParticipation,
        submittedAt: toUTCFormat(participant.createdAt) ?? "",
        startDate: participant.beganAt ? (toUTCFormat(participant.beganAt) ?? "") : "",
        endDate: participant.endedAt ? (toUTCFormat(participant.endedAt) ?? "") : "",
        creditsAwarded: participant.credit?.creditsAccepted ?? 0,
        role: participant.role ? renderParticipationRole(participant.role as ActivityParticipationRole) : undefined,
        attendanceType: participant.attendanceType
          ? renderAttendanceType(participant.attendanceType as AttendanceType)
          : undefined,
        managingSectionName: participant.managingSection?.name ?? null
      })
    );
  }, [data]);

  const hasActiveFilters = activeFilterCount > 0 || !!searchValue;

  const handleExportCurrentList = useCallback(() => {
    if (onExportCurrentList) {
      void onExportCurrentList();
      return;
    }
    const rows = participantsData.map((p: ParticipantRow) => exportRow(p));
    const csvContent = [exportHeaders, ...rows].map((row) => row.join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }));
    link.download = exportFilename;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [onExportCurrentList, participantsData, exportHeaders, exportRow, exportFilename]);

  const handleOpenPersonSheet = useCallback((pgaId: string) => {
    setSelectedPgaId(pgaId);
    setIsPersonSideSheetOpen(true);
  }, []);

  const encodedReturnUrl = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search}`),
    [location.pathname, location.search]
  );

  const participantsQueryRefetch = useMemo(
    () => ({
      query: GET_PARTICIPANTS,
      variables: { filters: queryFilters, orderBy: orderByVariable, ...paginationVariables }
    }),
    [queryFilters, orderByVariable, paginationVariables]
  );

  const handleEditParticipation = useCallback((participant: ParticipantRow) => {
    setSelectedParticipation(participant);
    setEditParticipationModalOpen(true);
  }, []);

  const handleDeleteParticipation = useCallback((participant: ParticipantRow) => {
    setSelectedParticipation(participant);
    setDeleteParticipationModalOpen(true);
  }, []);

  const handleCloseEditParticipationModal = useCallback(() => {
    setEditParticipationModalOpen(false);
    setSelectedParticipation(null);
  }, []);

  const handleCloseDeleteParticipationModal = useCallback(() => {
    setDeleteParticipationModalOpen(false);
    setSelectedParticipation(null);
  }, []);

  const nameColumn: ColumnDef<ParticipantRow, unknown> = {
    accessorKey: "name",
    header: "Participant",
    cell: ({ row }: CellContext<ParticipantRow, unknown>) => (
      <AvatarDetails
        picture={row.original.profilePhoto ?? undefined}
        title={row.original.name}
        text={`PGA ID: ${row.original.pgaId}`}
      />
    ),
    size: 300,
    meta: { headerClassName: "hide-header-in-column" }
  };

  const columns: ColumnDef<ParticipantRow, unknown>[] = useMemo(
    () => [
      nameColumn,
      ...extraColumns,
      buildActionColumn<ParticipantRow>({
        getActions: (row) =>
          buildParticipationActions({
            canManageParticipations: canManageParticipationActions,
            hasCredit: !!row.creditId,
            isTypeLevelParticipation: row.isTypeLevelParticipation,
            canEdit: row.canEdit,
            canDelete: row.canDelete,
            onEdit: () => handleEditParticipation(row),
            onDelete: () => handleDeleteParticipation(row)
          }),
        onViewClick: (row) => handleOpenPersonSheet(row.pgaId),
        viewButtonTitle: "View person details",
        actionId,
        setMenu
      })
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      extraColumns,
      canManageParticipationActions,
      handleDeleteParticipation,
      handleEditParticipation,
      handleOpenPersonSheet,
      setMenu
    ]
  );

  const headingActions = useMemo(
    () => [
      ...(additionalHeadingActions ?? []),
      {
        label: "Export Current List",
        icon: "download" as const,
        variant: "text" as const,
        disabled: isExportingCurrentList,
        onClick: handleExportCurrentList
      }
    ],
    [handleExportCurrentList, additionalHeadingActions, isExportingCurrentList]
  );

  return (
    <>
      <Menu />
      <TableHeading heading="Activity Participants" body={headingBody} actions={headingActions} />
      <Table
        data={participantsData}
        loading={participantsLoading}
        columns={columns}
        serverSideSorting
        sortingState={orderBy}
        onSortingChange={(updater) => {
          setOrderBy(updater);
          resetPagination();
        }}
        onClickRow={(row) => {
          navigate(
            `/activities/activity-types/${activityTypeId}/participants/${row.original.id}?returnUrl=${encodedReturnUrl}` as Path,
            {
              state: { participation: row.original }
            }
          );
        }}
        data-testid={testId}
        hasActiveFilters={hasActiveFilters}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Participants" caption={searchValue ? "Try a different search" : ""} iconName="person" />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Participants Found." caption={noDataCaption} iconName="person" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(data?.activityParticipations, {
              loading: participantsLoading
            })}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Participants"
              query={searchValue}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              onChange={(e) => {
                setSearchValue(e);
                resetPagination();
              }}
            />
            <div data-name="dateRange" data-filter-size="custom">
              <Controller
                name="dateRange"
                control={control}
                render={() => (
                  <DateSelect
                    key={`dateRange-${dateRange.startDate}-${dateRange.endDate}`}
                    label="Participation Date Range"
                    value={dateRange}
                    useModal={true}
                    placeholder="Show All"
                    onChange={(date: DateValueType | string | null) => {
                      if (date && typeof date === "object" && date.startDate && date.endDate) {
                        const start =
                          typeof date.startDate === "string"
                            ? date.startDate
                            : (date.startDate?.toISOString().split("T")[0] ?? "");
                        const end =
                          typeof date.endDate === "string"
                            ? date.endDate
                            : (date.endDate?.toISOString().split("T")[0] ?? "");
                        setDateRange({ startDate: start, endDate: end });
                        if (persistDateRange) {
                          addSearchParam("startDate", start);
                          addSearchParam("endDate", end);
                        }
                        resetPagination();
                      } else {
                        setDateRange({ startDate: "", endDate: "" });
                        if (persistDateRange) deleteSearchParams(["startDate", "endDate"]);
                        resetPagination();
                      }
                    }}
                  />
                )}
              />
            </div>
            {showRoleFilter && (
              <div data-name="roles">
                <MultiSelect
                  label="Role"
                  options={getParticipationRoleOptions(creditRates)}
                  selectedValues={selectedRoles}
                  onSelect={(value, checked) => {
                    setSelectedRoles((prev) => (checked ? [...prev, value] : prev.filter((r) => r !== value)));
                    resetPagination();
                  }}
                  onClear={() => {
                    setSelectedRoles([]);
                    resetPagination();
                  }}
                />
              </div>
            )}
            {showAttendanceTypeFilter && (
              <div data-name="attendanceTypes">
                <MultiSelect
                  label="Attendance Type"
                  options={getAttendanceTypeOptions(creditRates)}
                  selectedValues={selectedAttendanceTypes}
                  onSelect={(value, checked) => {
                    setSelectedAttendanceTypes((prev) =>
                      checked ? [...prev, value] : prev.filter((t) => t !== value)
                    );
                    resetPagination();
                  }}
                  onClear={() => {
                    setSelectedAttendanceTypes([]);
                    resetPagination();
                  }}
                />
              </div>
            )}
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(data?.activityParticipations, {
              loading: participantsLoading
            })}
            totalRowCount={data?.activityParticipations?.totalCount}
          />
        )}
      />

      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
      />
      {editParticipationModalOpen &&
        selectedParticipation &&
        (selectedParticipation.isTypeLevelParticipation || selectedParticipation.creditId) && (
          <EditParticipationModal
            onDismiss={handleCloseEditParticipationModal}
            pgaId={selectedParticipation.pgaId}
            activityId={selectedParticipation.creditId ?? ""}
            activityParticipationId={selectedParticipation.id}
            isTypeLevelParticipation={selectedParticipation.isTypeLevelParticipation}
            additionalRefetchQueries={[participantsQueryRefetch]}
          />
        )}
      {deleteParticipationModalOpen &&
        selectedParticipation &&
        (selectedParticipation.isTypeLevelParticipation || selectedParticipation.creditId) && (
          <DeleteParticipationModal
            onDismiss={handleCloseDeleteParticipationModal}
            activityId={selectedParticipation.creditId ?? undefined}
            activityParticipationId={selectedParticipation.id}
            isTypeLevelParticipation={selectedParticipation.isTypeLevelParticipation}
            onSuccessNavigate={() => {}}
            additionalRefetchQueries={[participantsQueryRefetch]}
          />
        )}
    </>
  );
}

function useOrderByParam(): [SortingState, OnChangeFn<SortingState>] {
  const { params, addSearchParam, deleteSearchParams } = useSearchParams();

  const sortState = useMemo((): SortingState => {
    const sortField = params.sortField as string | undefined;
    const sortDir = params.sortDir as string | undefined;
    if (!sortField) return [];
    return [{ id: sortField, desc: sortDir === "desc" }];
  }, [params.sortField, params.sortDir]);

  const setSortState: OnChangeFn<SortingState> = (updaterOrValue) => {
    const next = typeof updaterOrValue === "function" ? updaterOrValue(sortState) : updaterOrValue;
    const sort = next[0];
    if (sort) {
      addSearchParam("sortField", sort.id);
      addSearchParam("sortDir", sort.desc ? "desc" : "asc");
    } else {
      deleteSearchParams(["sortField", "sortDir"]);
    }
  };

  return [sortState, setSortState];
}
